/**
 * Universal Typography Configuration for Smart BI Dashboard
 * Minimum readable font size: 14px (0.875rem)
 * Absolute minimum: 12px (0.75rem) - only for metadata/timestamps
 */

export const TYPOGRAPHY_CONFIG = {
  // Base font sizes in pixels
  fontSizes: {
    xs: 12,      // Only for metadata, timestamps - use sparingly
    sm: 14,      // Minimum for readable text
    base: 16,    // Default body text
    lg: 18,      // Section headers
    xl: 20,      // Important values
    '2xl': 24,   // Page sections
    '3xl': 30,   // Main headings
    '4xl': 36,   // KPI values
    '5xl': 48,   // Hero numbers
  },

  // Font sizes in rem
  fontSizesRem: {
    xs: '0.75rem',     // 12px
    sm: '0.875rem',    // 14px
    base: '1rem',      // 16px
    lg: '1.125rem',    // 18px
    xl: '1.25rem',     // 20px
    '2xl': '1.5rem',   // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
  },

  // Line heights
  lineHeights: {
    xs: 1.2,
    sm: 1.4,
    base: 1.5,
    lg: 1.6,
    xl: 1.75,
  },

  // Font weights
  fontWeights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // Component-specific configurations
  components: {
    kpi: {
      value: { size: 36, weight: 700, lineHeight: 1.2 },
      label: { size: 16, weight: 500, lineHeight: 1.5 },
      trend: { size: 14, weight: 600, lineHeight: 1.4 },
      subtitle: { size: 14, weight: 400, lineHeight: 1.5 },
    },
    chart: {
      title: { size: 20, weight: 600, lineHeight: 1.5 },
      axisLabel: { size: 16, weight: 500, lineHeight: 1.4 },
      axisTick: { size: 20, weight: 500, lineHeight: 1.4 },
      legend: { size: 16, weight: 500, lineHeight: 1.5 },
      dataLabel: { size: 14, weight: 500, lineHeight: 1.2 },
      tooltip: {
        title: { size: 16, weight: 600, lineHeight: 1.4 },
        body: { size: 14, weight: 400, lineHeight: 1.5 },
      },
    },
    table: {
      header: { size: 16, weight: 600, lineHeight: 1.5 },
      cell: { size: 14, weight: 400, lineHeight: 1.5 },
      footer: { size: 14, weight: 500, lineHeight: 1.5 },
    },
    navigation: {
      item: { size: 16, weight: 500, lineHeight: 1.5 },
      subitem: { size: 14, weight: 400, lineHeight: 1.4 },
    },
    form: {
      label: { size: 16, weight: 500, lineHeight: 1.5 },
      input: { size: 16, weight: 400, lineHeight: 1.5 },
      helper: { size: 14, weight: 400, lineHeight: 1.4 },
      error: { size: 14, weight: 500, lineHeight: 1.4 },
    },
    button: {
      text: { size: 16, weight: 500, lineHeight: 1.2 },
      textSm: { size: 14, weight: 500, lineHeight: 1.2 },
    },
    badge: {
      text: { size: 14, weight: 500, lineHeight: 1.2 },
    },
    metadata: {
      // Only component allowed to use 12px
      text: { size: 12, weight: 400, lineHeight: 1.4 },
    },
  },

  // Chart.js specific configuration
  chartJs: {
    global: {
      defaultFontSize: 14,
      defaultFontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    scales: {
      ticks: {
        fontSize: 14,
        fontStyle: 'normal',
        padding: 8,
      },
      scaleLabel: {
        fontSize: 16,
        fontStyle: 'bold',
      },
    },
    legend: {
      labels: {
        fontSize: 16,
        fontStyle: 'normal',
        padding: 10,
      },
    },
    tooltips: {
      titleFontSize: 16,
      bodyFontSize: 14,
      footerFontSize: 14,
      titleFontStyle: 'bold',
      bodyFontStyle: 'normal',
    },
    title: {
      fontSize: 20,
      fontStyle: 'bold',
    },
  },

  // Tailwind class mappings
  tailwindClasses: {
    xs: 'text-xs',      // 12px - use sparingly
    sm: 'text-sm',      // 14px - minimum readable
    base: 'text-base',  // 16px
    lg: 'text-lg',      // 18px
    xl: 'text-xl',      // 20px
    '2xl': 'text-2xl',  // 24px
    '3xl': 'text-3xl',  // 30px
    '4xl': 'text-4xl',  // 36px
    '5xl': 'text-5xl',  // 48px
  },

  // Accessibility guidelines
  accessibility: {
    minimumReadableSize: 14,
    minimumMetadataSize: 12,
    highContrastMinimum: 16,
  },
};

// Helper function to get font style object
export const getFontStyle = (
  size: keyof typeof TYPOGRAPHY_CONFIG.fontSizes = 'base',
  weight: keyof typeof TYPOGRAPHY_CONFIG.fontWeights = 'normal',
  lineHeight?: keyof typeof TYPOGRAPHY_CONFIG.lineHeights
) => ({
  fontSize: TYPOGRAPHY_CONFIG.fontSizes[size],
  fontWeight: TYPOGRAPHY_CONFIG.fontWeights[weight],
  lineHeight: lineHeight ? TYPOGRAPHY_CONFIG.lineHeights[lineHeight] : undefined,
});

// Helper function to validate minimum font size
export const validateFontSize = (size: number): boolean => {
  return size >= TYPOGRAPHY_CONFIG.accessibility.minimumMetadataSize;
};

// Helper function to get component-specific font configuration
export const getComponentFontConfig = (
  component: keyof typeof TYPOGRAPHY_CONFIG.components,
  element: string
) => {
  const componentConfig = TYPOGRAPHY_CONFIG.components[component];
  return (componentConfig as any)[element] || TYPOGRAPHY_CONFIG.components.kpi.label;
};